<template>
  <div class="dashboard">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1>Dashboard</h1>
      <div class="d-flex">
        <button class="btn btn-primary" @click="refreshData">
          <i class="bi bi-arrow-clockwise me-1"></i> Actualizar
        </button>
      </div>
    </div>

    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <div v-else>
      <!-- Sección Estadísticas (primera fila) -->
      <div class="row g-4 align-items-start">
        <div class="col-md-6 col-lg-3">
          <div class="card bg-light-blue">
            <div class="card-body">
              <h5 class="card-title">Pacientes</h5>
              <div class="d-flex align-items-center">
                <div class="stat-icon">
                  <i class="bi bi-people-fill"></i>
                </div>
                <h2 class="ms-3 mb-0">{{ dashboardData.patients_count || 0 }}</h2>
              </div>
              <router-link to="/patients" class="card-link mt-3 d-block text-end">
                Ver todos <i class="bi bi-arrow-right"></i>
              </router-link>
            </div>
          </div>
        </div>

        <div class="col-md-6 col-lg-3">
          <div class="card bg-light-green">
            <div class="card-body">
              <h5 class="card-title">Citas</h5>
              <div class="d-flex align-items-center">
                <div class="stat-icon">
                  <i class="bi bi-calendar-check"></i>
                </div>
                <h2 class="ms-3 mb-0">{{ dashboardData.appointments_count || 0 }}</h2>
              </div>
              <router-link to="/appointments" class="card-link mt-3 d-block text-end">
                Ver todas <i class="bi bi-arrow-right"></i>
              </router-link>
            </div>
          </div>
        </div>

        <div class="col-md-6 col-lg-3">
          <div class="card bg-light-orange">
            <div class="card-body">
              <h5 class="card-title">Planes de Tratamiento</h5>
              <div class="d-flex align-items-center">
                <div class="stat-icon">
                  <i class="bi bi-clipboard2-pulse"></i>
                </div>
                <h2 class="ms-3 mb-0">{{ dashboardData.treatment_plans_count || 0 }}</h2>
              </div>
              <router-link to="/treatment-plans" class="card-link mt-3 d-block text-end">
                Ver todos <i class="bi bi-arrow-right"></i>
              </router-link>
            </div>
          </div>
        </div>

        <div class="col-md-6 col-lg-3">
          <div class="card bg-light-purple">
            <div class="card-body">
              <h5 class="card-title">Inventario</h5>
              <div class="d-flex align-items-center">
                <div class="stat-icon">
                  <i class="bi bi-box-seam"></i>
                </div>
                <div class="ms-3">
                  <h3 class="mb-0">{{ dashboardData.inventory_summary?.total_products || 0 }}</h3>
                  <div class="text-muted small">productos</div>
                </div>
              </div>
              <div class="mt-3">
                <div class="d-flex justify-content-between">
                  <span class="text-danger">
                    {{ dashboardData.inventory_summary?.low_stock_count || 0 }} con stock bajo
                  </span>
                  <span class="text-success">
                    {{ dashboardData.inventory_summary?.total_operations || 0 }} operaciones
                  </span>
                </div>
              </div>
              <router-link to="/inventory" class="card-link mt-3 d-block text-end">
                Ver detalles <i class="bi bi-arrow-right"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Fila: Próximas Citas + Pacientes Recientes -->
      <div class="row g-4 mt-4 align-items-start">
        <!-- Misma columna (col-md-12 col-lg-8) para Próximas Citas Y Recordatorios -->
        <div class="col-md-12 col-lg-8">
          <!-- Tarjeta: Próximas Citas -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Próximas Citas</h5>
            </div>
            <div class="card-body p-0">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Fecha</th>
                    <th>Hora</th>
                    <th>Tratamiento</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="appointments.length === 0">
                    <td colspan="5" class="text-center py-3">No hay próximas citas</td>
                  </tr>
                  <tr v-for="appointment in appointments" :key="appointment.id">
                    <td>{{ appointment.patient_name }}</td>
                    <td>{{ formatDate(appointment.fecha) }}</td>
                    <td>{{ formatTime(appointment.hora) }}</td>
                    <td>{{ appointment.tipo_tratamiento }}</td>
                    <td>
                      <span :class="getStatusClass(appointment.estado)" class="badge rounded-pill">
                        {{ appointment.estado }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="card-footer bg-white text-end">
              <router-link to="/appointments" class="btn btn-sm btn-primary">
                Ver todas las citas
              </router-link>
            </div>
          </div>

          <!-- Tarjeta: Recordatorios (en la MISMA columna, debajo de Próximas Citas) -->
          <div class="row my-4">
  <div class="col-12">
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Recordatorios</h5>
        </div>
        <div class="card-body p-0">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Fecha de Cita</th>
                <th>Tratamiento</th>
                <th>Fecha de Recordatorio</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="sortedReminders.length === 0">
                <td colspan="5" class="text-center py-3">
                  No hay recordatorios
                </td>
              </tr>
              <tr v-else v-for="reminder in sortedReminders" :key="reminder.id">
                <td>{{ reminder.patient_name }}</td>
                <td>{{ reminder.appointment_date }}</td>
                <td>{{ reminder.treatment }}</td>
                <td>{{ reminder.reminder_date }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
</div>
        </div>

        <!-- Pacientes Recientes -->
        <div class="col-md-12 col-lg-4">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Pacientes Recientes</h5>
            </div>
            <div class="card-body p-0">
              <ul class="list-group list-group-flush">
                <li v-if="patients.length === 0" class="list-group-item text-center py-3">
                  No hay pacientes recientes
                </li>
                <li v-for="patient in patients" :key="patient.id" class="list-group-item">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{{ patient.first_name }} {{ patient.last_name }}</strong>
                      <div class="text-muted small">{{ patient.phone_number || 'Sin teléfono' }}</div>
                    </div>
                    <router-link :to="`/patients/${patient.id}`" class="btn btn-sm btn-outline-primary">
                      Ver
                    </router-link>
                  </div>
                </li>
              </ul>
            </div>
            <div class="card-footer bg-white text-end">
              <router-link to="/patients" class="btn btn-sm btn-primary">
                Ver todos los pacientes
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>  
  </div>
</template>

<script>
// Asegúrate de importar o definir tu servicio de recordatorios
import { dashboardService, patientService, appointmentService, reminderService } from '@/services/api';
import { formatDateForDisplay } from '@/utils/dateUtils';

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: true,
      dashboardData: {
        patients_count: 0,
        appointments_count: 0,
        treatment_plans_count: 0,
        odontograms_count: 0,
        teeth_count: 0,
        inventory_summary: {
          total_products: 0,
          total_suppliers: 0,
          total_operations: 0,
          low_stock_count: 0,
          total_inventory_value: 0,
          latest_operation: {
            date: null,
            user: null
          }
        }
      },
      patients: [],
      appointments: [],
      reminders: []
    };
  },
  async created() {
    try {
      await this.refreshData();
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      this.loading = false;
    }
  },
  computed: {
    // Ordenar recordatorios por fecha ascendente
    sortedReminders() {
      return this.reminders.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
    }
  },
  methods: {
    async refreshData() {
      this.loading = true;
      try {
        // Obtener datos del dashboard
        const dashboardResponse = await dashboardService.getData();
        this.dashboardData = dashboardResponse.data;

        // Pacientes recientes
        const patientsResponse = await patientService.getAll({
          limit: 5,
          ordering: '-created_at'
        });
        this.patients = patientsResponse.data;

        // Próximas citas
        const appointmentsResponse = await appointmentService.getWithPatientData();
        const now = new Date();
        const upcomingAppointments = appointmentsResponse.data.filter(appointment => {
          if (!appointment.fecha || !appointment.hora) return false;
          const apptDateTime = new Date(`${appointment.fecha}T${appointment.hora}`);
          return apptDateTime >= now;
        });
        upcomingAppointments.sort((a, b) => {
          const aDate = new Date(`${a.fecha}T${a.hora}`);
          const bDate = new Date(`${b.fecha}T${b.hora}`);
          return aDate - bDate;
        });
        this.appointments = upcomingAppointments;

        // Recordatorios
        const remindersResponse = await reminderService.getAll();
        this.reminders = remindersResponse.data;
      } catch (error) {
        console.error('Error refreshing data:', error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      return formatDateForDisplay(dateString);
    },
    formatTime(timeString) {
      if (!timeString) return '';
      return timeString.substring(0, 5);
    },
    getStatusClass(status) {
      const statusClasses = {
        'programada': 'bg-info',
        'completada': 'bg-success',
        'inasistencia': 'bg-danger',
        'confirmada': 'bg-warning'
      };
      return statusClasses[status] || 'bg-secondary';
    }
  }
};
</script>

<style scoped>
.stat-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 1.5rem;
}

.bg-light-blue {
  background-color: rgba(13, 110, 253, 0.1);
}
.bg-light-blue .stat-icon {
  background-color: rgba(13, 110, 253, 0.2);
  color: #0d6efd;
}

.bg-light-green {
  background-color: rgba(25, 135, 84, 0.1);
}
.bg-light-green .stat-icon {
  background-color: rgba(25, 135, 84, 0.2);
  color: #198754;
}

.bg-light-orange {
  background-color: rgba(255, 136, 0, 0.1);
}
.bg-light-orange .stat-icon {
  background-color: rgba(255, 136, 0, 0.2);
  color: #ff8800;
}

.bg-light-purple {
  background-color: rgba(138, 43, 226, 0.1);
}
.bg-light-purple .stat-icon {
  background-color: rgba(138, 43, 226, 0.2);
  color: #8a2be2;
}
</style>
